import {
  axiosClient,
  buildQuery,
  createNote,
  createNoteAttachment,
  createTempFileFromUrl,
  getNote,
  getNoteAttachments,
  getNotePreview,
  getNotes,
  getNoteText,
  getSession,
  updateNoteAttachmentName,
} from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import {
  GetNotesResponse,
  NewNoteData,
  NoteAttachmentDto,
  NoteDto,
  NotePreviewDto,
  NoteServiceShareDto,
  NoteTextDto,
  sessionFromDto,
} from '@nx-frontend/shared/types';

export interface GetNotesPayload {
  userServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  workspaceId: string;
  counters: boolean;
  count: boolean;
  type?: string | null;
  order?: string[][] | null;
  offset?: number | null;
  limit?: number | null;
  title?: string | null;
  globalId?: string | null;
  parentId?: string | null;
}
interface NoteQuery {
  counters: boolean;
  count: boolean;
  filter?: {
    type?: string;
    globalId?: string;
    parentId?: string;
    title?: { $like: string };
  };
  order?: string[][];
  range?: {
    limit?: number;
    offset?: number;
  };
}

export const filterNotes = async (payload: GetNotesPayload): Promise<GetNotesResponse> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  const { counters, count, type, order, limit, offset, title, parentId, globalId } = payload;
  const query: NoteQuery = {
    counters,
    count,
  };
  if (type || title || parentId || globalId) {
    if (!('filter' in query)) {
      query.filter = {};
    }
    if ('object' === typeof query.filter) {
      if (type) {
        query.filter.type = type;
      }
      if (title) {
        query.filter.title = { $like: `%${title}%` };
      }
      if (parentId) {
        query.filter.parentId = parentId;
      }
      if (globalId) {
        query.filter.globalId = globalId;
      }
    }
  }
  if (order) {
    query.order = order;
  }
  if (limit || offset) {
    if (!('range' in query)) {
      query.range = {};
    }
    if ('object' === typeof query.range) {
      if (limit) {
        query.range.limit = limit;
      }
      if (offset) {
        query.range.offset = offset;
      }
    }
  }
  return await getNotes({
    host: noteServiceHost,
    userId,
    workspaceId,
    query: buildQuery(query),
  });
};

export interface GetNotePayload {
  userServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  workspaceId: string;
  noteId: string;
}
export const retrieveNote = async (payload: GetNotePayload): Promise<NoteDto> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId, noteId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  return await getNote({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteId,
  });
};

export const retrieveNoteAttachments = async (payload: GetNotePayload): Promise<NoteAttachmentDto[]> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId, noteId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  return await getNoteAttachments({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteId,
  });
};

export interface CreateNotePayload {
  userServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  workspaceId: string;
  noteId: string;
  note: NewNoteData;
}
export const addNewNote = async (payload: CreateNotePayload): Promise<NoteDto> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId, noteId, note } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  return await createNote({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteId,
    note,
  });
};

export interface NoteEnriched extends NoteDto {
  text: NoteTextDto;
  preview: NotePreviewDto | null;
}
export const retrieveNoteEnriched = async (payload: GetNotePayload): Promise<NoteEnriched> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId, noteId } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  const note = await getNote({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteId,
  });
  const text = await getNoteText({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteId,
  });
  let preview: NotePreviewDto | null = null;
  try {
    preview = await getNotePreview({
      host: noteServiceHost,
      userId,
      workspaceId,
      noteId,
    });
  } catch (err) {
    preview = null;
  }
  return { ...note, ...{ text }, ...{ preview } };
};

export interface UpdateNoteAttachmentPayload {
  userServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  workspaceId: string;
  attachmentId: string;
  displayName: string;
}
export const updateNoteAttachmentDisplayName = async (payload: UpdateNoteAttachmentPayload): Promise<NoteDto> => {
  const { userServiceHost, noteServiceHost, sessionId, workspaceId, attachmentId, displayName } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  return await updateNoteAttachmentName({
    host: noteServiceHost,
    userId,
    workspaceId,
    attachmentId,
    displayName,
  });
};

export interface CreateAttachmentPayload {
  userServiceHost: string;
  noteServiceHost: string;
  sessionId: string;
  workspaceId: string;
  attachmentId: string;
  noteGlobalId: string;
  source: { tempFileName: string };
  displayName: string;
  mime: string;
  inList: boolean;
  role: string;
  createSource: string;
  emitEvent: boolean;
}
export const createAttachment = async (payload: CreateAttachmentPayload): Promise<NoteAttachmentDto> => {
  const {
    userServiceHost,
    noteServiceHost,
    sessionId,
    workspaceId,
    noteGlobalId,
    attachmentId,
    source,
    displayName,
    mime,
    inList,
    role,
    createSource,
    emitEvent,
  } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  return await createNoteAttachment({
    host: noteServiceHost,
    userId,
    workspaceId,
    noteGlobalId,
    attachmentId,
    source,
    displayName,
    mime,
    inList,
    role,
    createSource,
    emitEvent,
  });
};

interface CopyAttachmentPayload {
  userServiceHost: string;
  noteServiceHost: string;
  fileServiceHost: string;
  sessionId: string;
  source: {
    tempFileName?: string;
    attachmentId?: string;
    workspaceId?: string;
    externalUrl?: string;
    shareId?: string;
    shareSecret?: string;
  };
  isImport?: string;
  inList: boolean;
  noteId?: string;
  folderId?: string;
  workspaceId: string;
  noteGlobalId: string;
  attachmentId: string;
  role: string;
  emitEvent: boolean;
  blotName?: string;
}
export const copyAttachment = async (payload: CopyAttachmentPayload): Promise<NoteAttachmentDto> => {
  const {
    userServiceHost,
    noteServiceHost,
    fileServiceHost,
    sessionId,
    source,
    inList,
    workspaceId,
    noteGlobalId,
    attachmentId,
    role,
    emitEvent,
    blotName,
  } = payload;
  const { userId } = sessionFromDto(await getSession({ host: userServiceHost, sessionId }));
  if (source.externalUrl) {
    const tempFile = await createTempFileFromUrl({
      host: fileServiceHost,
      userId,
      url: source.externalUrl,
    });
    return createNoteAttachment({
      host: noteServiceHost,
      userId,
      workspaceId,
      noteGlobalId,
      attachmentId,
      source: {
        tempFileName: tempFile.name,
      },
      displayName: encodeURIComponent(tempFile.filename),
      mime: tempFile.type,
      inList,
      role,
      createSource: blotName === 'uploader' ? 'uploader_webclient' : 'default',
      emitEvent,
    });
  } else {
    return createNoteAttachment({
      host: noteServiceHost,
      userId,
      workspaceId,
      noteGlobalId,
      attachmentId,
      source: {
        attachmentGlobalId: source.attachmentId,
        workspaceId: source.workspaceId,
        shareId: source.shareId,
        shareSecurityKey: source.shareSecret,
      },
      inList: false,
      role: role,
      createSource: blotName === 'uploader' ? 'uploader_webclient' : 'default',
    });
  }
};

export interface ShareNotePayload {
  userId: number;
  workspaceId: string;
  noteId: string;
  options: {
    password?: string;
    accessControl?: string;
    type: 'web' | 'portal';
  };
}

export async function shareNote({
  userId,
  workspaceId,
  noteId,
  options,
}: ShareNotePayload): Promise<NoteServiceShareDto> {
  const host = generalEnv.noteServiceUrl;
  const { data } = await axiosClient.post<NoteServiceShareDto>(
    `${host}/v1/workspaces/${workspaceId}/notes/${noteId}/share`,
    options,
    {
      userId,
    }
  );
  return data;
}
